/**
 * @author      yewon@zumper.com (Yewon Lee)
 * @copyright   Copyright (c) 2018, Zumper
 * @description NavbarDesktop container
 */

import { getSignInUrl, getSignUpUrl } from '@zcode-ui/account'
import {
  MAX_FILTER_PRICE,
  MIN_FILTER_PRICE,
  handleFilterChange,
  selectToggleValue,
  updateFilters,
} from '@zcode-ui/filters'
import { guessMapDimensions, selectMapDimensions } from '@zcode-ui/map'
import { checkLoggedIn, getUser, selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import {
  Header,
  type HeaderProps,
  NAV_SELECTIONS,
  getCommaSeparatedText,
} from '@zcode-ui/zumper-header'
import { type AutocompleteDetailLocationResult, AutocompleteResult } from '@zcode/bizos'
import { memoizeState } from '@zcode/memoizers'
import { EVENT_NAMES, PAGE_NAMES, PROP_NAMES, type Payload, SECTIONS, track } from '@zcode/mixpanel'
import { AnalyticsC, definitelyError, trackGAEvent } from '@zcode/nr-utils'
import { locate } from '@zcode/nr/es/shared/api/geos'
import { defaultFilters } from '@zcode/nr/es/shared/bizos/filter'
import {
  AUTOCOMPLETE_ADDRESS,
  AUTOCOMPLETE_POSTCODE,
} from '@zcode/nr/es/shared/components/Autocomplete/constants'
import { getResultFromHistoryState } from '@zcode/nr/es/shared/components/Autocomplete/helpers/getResultFromHistoryState'
import {
  type PageNameValues,
  trackSearchBarClick,
} from '@zcode/nr/es/shared/components/Autocomplete/trackSearchBarClick'
import {
  hideMobileAutocomplete,
  showMobileAutocomplete,
} from '@zcode/nr/es/shared/modules/autocomplete/actions'
import { selectShowMobileAutocomplete } from '@zcode/nr/es/shared/modules/autocomplete/selectors'
import { selectUser } from '@zcode/nr/es/shared/modules/user/selectors'
import deepclone from '@zcode/nr/es/shared/utils/deepclone'
import Marketing from '@zcode/nr/es/shared/utils/marketing'
import { hocCompose, memoMapDispatchToProps, withErrorBoundary } from '@zcode/react-utils'
import { routerConnect } from '@zcode/router-connect'
import type { RouteComponentProps } from '@zcode/router-utils'
import { MODAL_TYPE, type State } from '@zcode/state-type'
import type { MaybeEmpty } from '@zcode/utility-types'
import { toSlug } from '@zcode/utils'
import type { Location } from 'history'
import React from 'react'
import { connect } from 'react-redux'
import { useLocation } from 'react-router-dom-v5-compat'
import invariant from 'tiny-invariant'
import type { O } from 'ts-toolbelt'
import { focusNavbarSearch, updateSearchUI } from '../../actions'
import { logout } from '../../actions/bundle.action'
import { SEARCH_VIEW_TYPE } from '../../actions/constants'
import {
  checkDetailView,
  checkFavoritesView,
  checkHomepageView,
  checkMessagedView,
  checkPermanentAddressView,
  checkSavedSearchesView,
  isFiltersViewState,
  selectIsCurrentSearchView,
  selectIsMessaging3Active,
  selectIsRailPage,
} from '../../inspectors/index'
import { logger } from '../../logger'
import { default as navigateToAutocompleteResult } from '../../modules/autocomplete/thunks/navigateToAutocompleteResult'
import {
  selectCityName,
  selectHoodName,
  selectLocation,
  selectUrl,
} from '../../modules/currentSearch/modules/location/selectors'
import { selectEntity } from '../../modules/detail/modules/entity/selectors/index'
import {
  selectFilterEndDate,
  selectFilterStartDate,
  selectFilters,
} from '../../modules/filters/selectors/index'
import { selectStreamUnreadCount } from '../../modules/messages/selectors'
import { getPageNameFromRouteProps } from '../../modules/mixpanel/helpers'
import { mixpanelAutocompleteResultClick } from '../../modules/mixpanel/thunks/mixpanelAutocompleteResultClick'
import { mixpanelCompleteSearch } from '../../modules/mixpanel/thunks/mixpanelCompleteSearch'
import { mixpanelNavClick } from '../../modules/mixpanel/thunks/mixpanelNavClick'
import { mixpanelSwitchLeaseLength } from '../../modules/mixpanel/thunks/mixpanelSwitchLeaseLength'
import { mixpanelViewPage } from '../../modules/mixpanel/thunks/mixpanelViewPage'
import { receiveData as receiveModalsData } from '../../modules/modals/actions/saveSearchModal'
import { openModal } from '../../modules/modals/thunks/index'
import { removeSavedSearch } from '../../modules/modals/thunks/savedSearchOptionModal/removeSavedSearch'
import { setNavbarSticky } from '../../modules/navbar/actions/index'
import { selectMobileViewType } from '../../modules/navbar/selectors/index'
import { createSearch } from '../../modules/savedSearchesView/thunks/createSearch/createSearch'
import { selectRenterUnreadCount } from '../../modules/stream/selectors/index'
import { trackNavBarClick } from '../../modules/traktor/thunks/trackNavBarClick'
import type { Dispatch } from '../../modules/types'
import { withoutRouteProps } from '../../utils/withoutRouteProps'

interface OwnProps extends RouteComponentProps, Pick<HeaderProps, 'isHomepageView'> {
  location: Location
  origin?: string
  pageName?: string
}

const mapRouteToProps = (
  routeProps: RouteComponentProps,
  { initialLeaseLength }: Pick<HeaderProps, 'initialLeaseLength'>
) => {
  return {
    isCurrentSearchView: selectIsCurrentSearchView(routeProps),
    isDetailView: checkDetailView(routeProps),
    isFavoritesView: checkFavoritesView(routeProps),
    isHomepageView: checkHomepageView(routeProps),
    isMessagedView: checkMessagedView(routeProps),
    initialLeaseLength:
      !initialLeaseLength && checkPermanentAddressView(routeProps)
        ? NAV_SELECTIONS.LONG_TERM
        : initialLeaseLength,
    isSavedSearchesView: checkSavedSearchesView(routeProps),
    isRailPage: selectIsRailPage(routeProps),
  }
}

const selectDetailLocationObj = memoizeState(
  (entity: Exclude<ReturnType<typeof selectEntity>, null>) => {
    const detailLocationObj = entity
      ? new AutocompleteResult({
          city: entity.city,
          country: entity.country,
          is_derived_from_entity: true,
          lat: entity.lat,
          lng: entity.lng,
          name: entity.city,
          record_type: 'city',
          state: entity.state,
          url:
            entity.cityState || (entity.city && entity.state && toSlug(entity.city, entity.state)),
        } as const)
      : undefined
    assertIsDetailLocationObjOrUndefined(detailLocationObj)
    return detailLocationObj
  }
)

function assertIsDetailLocationObjOrUndefined(
  detailLocationObj: AutocompleteResult | undefined
): asserts detailLocationObj is AutocompleteDetailLocationResult | undefined {
  invariant(!detailLocationObj || !!detailLocationObj.isDerivedFromEntity)
}

const mapStateToProps = (state: State, ownProps: OwnProps) => {
  const { avatarName, fullName, toolsActivated } = getUser(state)
  const entity = selectEntity(state)

  const hoodName = selectHoodName(state) ?? ''
  const location = selectLocation(state)
  const selectedResult: MaybeEmpty<ReturnType<typeof getResultFromHistoryState>> =
    getResultFromHistoryState(ownProps.location) ?? {}
  const { city: fallbackCity, name, state: fallbackState, type } = selectedResult
  const detailLocationObj = selectDetailLocationObj(entity ?? undefined)
  const cityName = selectCityName(state) ?? fallbackCity ?? ''
  const stateName = location?.stateName ?? fallbackState
  const cityAndState = getCommaSeparatedText(cityName, stateName)
  const detailCityAndState = getCommaSeparatedText(entity?.city, entity?.state)
  const fullAddress = getCommaSeparatedText(name, cityAndState)
  const hoodCityAndState = getCommaSeparatedText(hoodName, cityAndState)
  const newAutoCompletLocationText =
    type === AUTOCOMPLETE_ADDRESS || type === AUTOCOMPLETE_POSTCODE ? fullAddress : hoodCityAndState
  const locationName = newAutoCompletLocationText
  const isMobile = selectIsMobile(state)
  return {
    avatarName,
    city: cityName,
    cityAndState,
    detailCity: entity?.city,
    detailCityAndState,
    detailLocationObj,
    fullName,
    getPageNameFromRouteProps,
    isLoggedIn: checkLoggedIn(state),
    isMessage3: selectIsMessaging3Active(state),
    isMobile: isMobile ?? false,
    locationName,
    toolsActivated,
    showMobileAutocomplete: selectShowMobileAutocomplete(state),
    isFiltersView: isFiltersViewState(state),
    mobileViewType: selectMobileViewType(state),
    filters: selectFilters(state) || {},
    filterStartDate: selectFilterStartDate(state),
    filterEndDate: selectFilterEndDate(state),
    unreadCount: selectRenterUnreadCount(state) ?? selectStreamUnreadCount(state),
    url: selectUrl(state),
  } satisfies Partial<HeaderProps>
}

const mapDispatchToProps = memoMapDispatchToProps(
  (
    dispatch: Dispatch,
    ownProps: Pick<OwnProps, 'origin' | 'pageName'>
  ): Pick<
    HeaderProps,
    | 'dispatchCreateSearch'
    | 'dispatchMixpanelAutocompleteResultClick'
    | 'dispatchMixpanelCompleteSearch'
    | 'dispatchMixpanelNavClick'
    | 'dispatchMixpanelViewPage'
    | 'dispatchRemoveSavedSearch'
    | 'dispatchSetNavbarSticky'
    | 'dispatchUpdateFilter'
    | 'getCurrentLocationName'
    | 'handleStayLengthChange'
    | 'handleOnSearchBarClick'
    | 'hideMobileAutocomplete'
    | 'onLoginClick'
    | 'onLogoutClick'
    | 'onManageClick'
    | 'onNavLinkClick'
    | 'onSaveSearchModalOpen'
    | 'onSearchClick'
    | 'onSelectLeaseLength'
    | 'onSelectAutocompleteResult'
    | 'onShowMainView'
  > => {
    return {
      dispatchCreateSearch: async (...args: Parameters<typeof createSearch>) =>
        await dispatch(createSearch(...args)),
      dispatchRemoveSavedSearch: (searchId) => {
        dispatch((_, getState) => {
          const state = getState()
          const user = selectUser(state)
          dispatch(
            removeSavedSearch({
              searchId,
              userId: user.userId,
              email: user.email,
            })
          )
        })
      },
      onSaveSearchModalOpen: ({ filters, origin }) => {
        dispatch((_, getState) => {
          const entity = selectEntity(getState())
          dispatch(
            receiveModalsData({
              entity,
              filters,
              origin,
            })
          )
          dispatch(openModal(MODAL_TYPE.SAVE_SEARCH))
        })
      },
      hideMobileAutocomplete: () => dispatch(hideMobileAutocomplete()),
      getCurrentLocationName: async (results) => {
        const { lat, lng } = results
        if (lat && lng) {
          return await locate({ lat, lng })
            .then((geos) => {
              const { city } = geos

              return city
            })
            .catch((error) => {
              logger.warn(
                'error getting geos from lat and lng',
                { lat, lng },
                definitelyError('error getting geos from lat and lng', error)
              )
            })
        }
      },
      onNavLinkClick: ({ trackAction, trackCity, linkClicked, analyticsEvent }) => {
        if (trackAction) {
          dispatch(trackNavBarClick({ action: trackAction, city: trackCity }))

          const payload: Payload = {
            [PROP_NAMES.NAVBAR_LINK_TYPE]: trackAction,
          }
          if (linkClicked) {
            payload[PROP_NAMES.LINK_CLICKED] = linkClicked
          }
          track({
            event: EVENT_NAMES.CLICK_NAVIGATION_LINK,
            payload,
          })
        }

        if (analyticsEvent) {
          trackGAEvent(AnalyticsC.NAVBAR, analyticsEvent, ownProps.origin)
        }
      },
      onLoginClick: () => {
        trackGAEvent(AnalyticsC.NAVBAR, 'Sign In', ownProps.origin)
      },
      onLogoutClick: () => {
        logout(ownProps.origin)
        trackGAEvent(AnalyticsC.NAVBAR, 'Logout', ownProps.origin)
      },
      onSearchClick: () => {
        dispatch(showMobileAutocomplete())
        dispatch(focusNavbarSearch())
        trackGAEvent(AnalyticsC.NAVBAR, 'Search', ownProps.origin)
      },
      onManageClick: () => {
        trackGAEvent(AnalyticsC.NAVBAR, 'Manage Rentals', ownProps.origin)
      },
      onShowMainView: () => {
        dispatch(
          // kill this generic action and and create an action for a specific usage
          updateSearchUI({
            key: 'searchView',
            value: SEARCH_VIEW_TYPE.MAIN,
          })
        )
      },
      onSelectAutocompleteResult: (result, filters) => {
        void dispatch(async (_, getState) => {
          // we need to clear neighborhood ids when navigating to a new location
          const scrubbedFilters = { ...defaultFilters, ...filters }
          scrubbedFilters.neighborhoodIds = []
          track({
            event: EVENT_NAMES.CLICK,
            payload: {
              [PROP_NAMES.PAGE_NAME]: PAGE_NAMES.HOMEPAGE,
              [PROP_NAMES.SECTION]: SECTIONS.SET_LOCATION,
              [PROP_NAMES.CITY_STATE_LOCATION]: [result.city, result.state]
                .filter(Boolean)
                .join(', '),
            },
          })
          const state = getState()
          const isMobile = selectIsMobile(state)
          if (isMobile) {
            dispatch(hideMobileAutocomplete())
          }
          // returning a promise here in order to chain dispatches to local autocomplete redux changes
          // as this function dispatches changes to NZR redux
          return await new Promise<void>((resolve) => {
            const toggleValue = selectToggleValue(state)
            const isFullWidthMap = toggleValue === 'full-map'
            // grab the dimensions from the map; only populated if we've been to CurrentSearchView before
            const mapDimensions = selectMapDimensions(state)
            const { height, width } = guessMapDimensions(mapDimensions, isFullWidthMap)
            void dispatch(navigateToAutocompleteResult({ result, height, width }))
            dispatch(updateFilters({ filters: scrubbedFilters, replace: true }))
            resolve()
          })
        })
      },
      onSelectLeaseLength: ({ leaseLength, sectionName }) => {
        const { pageName } = ownProps
        dispatch(mixpanelSwitchLeaseLength({ leaseLength, pageName, sectionName }))
        dispatch((_, getState) => {
          const state = getState()
          const { email, firstName, lastName, phone, userId } = selectUser(state)

          // @ts-expect-error we're missing moveIn, joinedAt, ...
          Marketing.trackIdentify({ email, firstName, lastName, phone, userId, leaseLength })
        })
      },
      dispatchUpdateFilter: (newFilters) => {
        dispatch((_, getState) => {
          const state = getState()
          const filters = selectFilters(state)
          const filtersCopy = deepclone(filters)
          dispatch(handleFilterChange({ ...filtersCopy, ...newFilters }))
        })
      },
      dispatchSetNavbarSticky: (isSticky) => {
        dispatch(setNavbarSticky(isSticky))
      },
      dispatchMixpanelViewPage: () => {
        dispatch(
          mixpanelViewPage({
            pageName: PAGE_NAMES.EXPANDED_GLOBAL_SEARCH,
          })
        )
      },
      dispatchMixpanelNavClick: ({ section, sectionTarget, buttonClicked, isMobile }) => {
        dispatch(
          mixpanelNavClick({
            section,
            sectionTarget,
            buttonClicked,
            pageName: PAGE_NAMES.EXPANDED_GLOBAL_SEARCH,
            includePreviousPage: isMobile,
          })
        )
      },
      dispatchMixpanelCompleteSearch: (leaseLength) => {
        dispatch(
          mixpanelCompleteSearch({
            pageName: PAGE_NAMES.EXPANDED_GLOBAL_SEARCH,
            section: 'Global Search Bar',
            leaseLengthType: leaseLength === 'Long-term' ? 'Long Term' : 'Short Term',
          })
        )
      },
      dispatchMixpanelAutocompleteResultClick: ({ result, index }) => {
        dispatch(mixpanelAutocompleteResultClick({ result, index }))
      },
      handleStayLengthChange: (term: NAV_SELECTIONS) => {
        dispatch((_, getState) => {
          const filters = selectFilters(getState())
          const filtersClone: O.Writable<typeof filters> = JSON.parse(JSON.stringify(filters))
          if (term === NAV_SELECTIONS.LONG_TERM) {
            filtersClone.longTerm = true
            filtersClone.shortTerm = null
            filtersClone.guests = null
            filtersClone.startDate = null
            filtersClone.endDate = null
            filtersClone.cats = null
            filtersClone.dogs = null
          } else if (term === NAV_SELECTIONS.SHORT_TERM) {
            filtersClone.shortTerm = true
            filtersClone.longTerm = null
            filtersClone.maxPrice = MAX_FILTER_PRICE
            filtersClone.minPrice = MIN_FILTER_PRICE
            filtersClone.bathroomsRange = defaultFilters.bathroomsRange
            filtersClone.bedrooms = defaultFilters.bedrooms
          }
          dispatch(handleFilterChange(filtersClone))
        })
      },
      handleOnSearchBarClick: (pageName: PageNameValues) => {
        dispatch(trackSearchBarClick({ pageName }))
      },
    } satisfies Partial<HeaderProps>
  },
  ['origin' as const, 'pageName' as const]
)

export const NavbarContainer = hocCompose(
  routerConnect(mapRouteToProps, { forwardRouteProps: true }),
  connect(mapStateToProps, mapDispatchToProps),
  withoutRouteProps,
  withErrorBoundary({ displayName: 'Navbar' }),
  (props: Omit<HeaderProps, 'signInHref' | 'signUpHref'>) => {
    const location = useLocation()
    return (
      <Header signInHref={getSignInUrl(location)} signUpHref={getSignUpUrl(location)} {...props} />
    )
  }
)
